import { useIsAdmin } from '../auth/hooks/useIsAdmin';

import {
  Grid,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
  Checkbox,
} from '@mui/material';
import GreenButton from '../../components/GreenButton';
import { Fragment, useState } from 'react';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { useAppSelector } from 'app/hooks';
import WeatherAlertDialog from './WeatherAlertDialog';
import { WeatherAlert } from 'types';
import {
  convertNumberFromDB,
  convertToImperial,
  getUnitForMeasurementType,
} from 'features/weather/utils/dataConverters';
import { getLabelForMeasurementType } from './utils/dataConverters';
import { grey } from '@mui/material/colors';
import WeatherAlertCloneDialog from './WeatherAlertCloneDialog';
import { useDishes } from 'features/dishes/api/getDishes';
import { useUpdateWeatherAlert } from './api/updateWeatherAlert';
import { useDeleteWeatherAlert } from './api/deleteWeatherAlert';

const WeatherAlertManagementPage = () => {
  const useImperial = useAppSelector(
    (state) => state.auth?.user?.us_customary_units
  );
  const isAdmin = useIsAdmin();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { data: dishes } = useDishes({});
  const updateAlert = useUpdateWeatherAlert({});
  const deleteAlert = useDeleteWeatherAlert({});
  const [selectedAlert, setSelectedAlert] = useState<undefined | WeatherAlert>(
    undefined
  );

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedAlert(undefined);
  };
  const onSubmitDelete = async () => {
    if (selectedAlert) await deleteAlert.mutateAsync(selectedAlert.id);
    setSelectedAlert(undefined);
  };
  const onCloseDelete = () => {
    setDeleteDialogOpen(false);
  };
  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };
  const handleNew = () => {
    setDialogOpen(true);
  };
  const handleClone = () => {
    setCloneDialogOpen(true);
  };
  const handleCloneClose = () => {
    setCloneDialogOpen(false);
    setSelectedAlert(undefined);
  };

  const handleToggle = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    changedAlert: WeatherAlert
  ) => {
    event.stopPropagation();
    await updateAlert.mutateAsync({
      data: {
        enabled: !changedAlert.enabled,
      },
      alertId: changedAlert.id,
    });
  };

  if (!isAdmin) return null;

  return (
    <Grid container spacing={2}>
      <Grid container item xs={12} alignItems='center' spacing={1}>
        <Grid item>
          <GreenButton name='New Alert' onClick={handleNew}></GreenButton>
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            color='primary'
            onClick={() => setDialogOpen(true)}
            disabled={!selectedAlert}
          >
            Edit Alert
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            color='primary'
            disabled={!selectedAlert}
            onClick={handleClone}
          >
            Clone Alert
          </Button>
        </Grid>
        <Grid item>
          <Button
            variant='contained'
            color='error'
            disabled={!selectedAlert}
            onClick={handleDelete}
          >
            Delete Alert
          </Button>
        </Grid>
      </Grid>
      <Grid item container>
        <Grid item component={Paper} xs>
          <List>
            {dishes?.map((dish, index) => (
              <Fragment key={index}>
                <Grid container>
                  <ListSubheader>{dish.name}</ListSubheader>
                </Grid>
                {dish.weatherAlerts.map((alert) => (
                  <ListItem
                    button
                    key={alert.id}
                    onClick={() => setSelectedAlert(alert)}
                    sx={{ pl: '2rem' }}
                    selected={alert.id === selectedAlert?.id}
                  >
                    <Checkbox
                      edge='start'
                      tabIndex={-1}
                      checked={!!alert.enabled}
                      onClick={(event) => handleToggle(event, alert)}
                      sx={{ pr: '2rem' }}
                    />
                    <ListItemText>
                      <Typography component='span'>
                        {getLabelForMeasurementType(alert.condition)}{' '}
                      </Typography>
                      <Typography
                        sx={{ ml: '1.5rem' }}
                        color='orange'
                        component='span'
                      >
                        Caution:{' '}
                      </Typography>
                      {alert.caution ? (
                        <Typography component='span'>
                          {convertToImperial(
                            convertNumberFromDB(alert.caution, alert.condition),
                            alert.condition,
                            useImperial
                          )?.toFixed(2) +
                            ' ' +
                            getUnitForMeasurementType(
                              alert.condition,
                              useImperial
                            )}
                        </Typography>
                      ) : (
                        <Typography component='span'>None</Typography>
                      )}
                      <Typography
                        color='red'
                        sx={{ ml: '1.5rem' }}
                        component='span'
                      >
                        Warning:{' '}
                      </Typography>

                      <Typography component='span'>
                        {convertToImperial(
                          convertNumberFromDB(alert.warning, alert.condition),
                          alert.condition,
                          useImperial
                        )?.toFixed(2) +
                          ' ' +
                          getUnitForMeasurementType(
                            alert.condition,
                            useImperial
                          )}
                      </Typography>
                    </ListItemText>
                  </ListItem>
                ))}
                {dish.weatherAlerts.length === 0 && (
                  <ListItem sx={{ pl: '2rem' }}>
                    <Typography color={grey[500]}>
                      No alerts set for this dish.
                    </Typography>
                  </ListItem>
                )}
              </Fragment>
            ))}
          </List>
        </Grid>
      </Grid>
      <WeatherAlertCloneDialog
        open={cloneDialogOpen}
        handleClose={handleCloneClose}
        weatherAlert={selectedAlert}
      />
      <WeatherAlertDialog
        open={dialogOpen}
        handleClose={handleClose}
        weatherAlert={selectedAlert}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={'Alert'}
        onSubmit={onSubmitDelete}
        onClose={onCloseDelete}
      />
    </Grid>
  );
};

export default WeatherAlertManagementPage;
